import React, { useState } from 'react';
import {
  Fab,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp as TradingIcon,
  History as HistoryIcon,
  Code as CodeIcon,
  Assessment as AssessmentIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../store';
import {
  selectTradingHistory,
  selectActiveTradingAnalysis,
  clearTradingHistory,
} from '../store';
import TradingHistoryPanel from './TradingHistoryPanel';

interface TradingFABProps {
  onToggleHistory?: (open: boolean) => void;
}

const TradingFAB: React.FC<TradingFABProps> = ({ onToggleHistory }) => {
  const dispatch = useAppDispatch();
  const tradingHistory = useAppSelector(selectTradingHistory);
  const activeAnalysis = useAppSelector(selectActiveTradingAnalysis);
  
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleOpenHistory = () => {
    setHistoryPanelOpen(true);
    onToggleHistory?.(true);
    handleMenuClose();
  };

  const handleCloseHistory = () => {
    setHistoryPanelOpen(false);
    onToggleHistory?.(false);
  };

  const handleClearHistory = () => {
    dispatch(clearTradingHistory());
    handleMenuClose();
  };

  const menuOpen = Boolean(menuAnchor);
  const hasHistory = tradingHistory.length > 0;
  const isAnalyzing = activeAnalysis?.status === 'in_progress';

  return (
    <>
      <Tooltip title="Trading Analysis" placement="left">
        <Fab
          color="primary"
          size="medium"
          onClick={hasHistory ? handleMenuOpen : handleOpenHistory}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 24,
            zIndex: 1000,
            background: isAnalyzing 
              ? 'linear-gradient(45deg, #ff9800 30%, #ffb74d 90%)'
              : 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            '&:hover': {
              background: isAnalyzing
                ? 'linear-gradient(45deg, #f57c00 30%, #ff9800 90%)'
                : 'linear-gradient(45deg, #1976D2 30%, #1EAEDB 90%)',
            },
            animation: isAnalyzing ? 'pulse 2s infinite' : 'none',
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                boxShadow: '0 0 0 0 rgba(33, 150, 243, 0.7)',
              },
              '70%': {
                transform: 'scale(1.05)',
                boxShadow: '0 0 0 10px rgba(33, 150, 243, 0)',
              },
              '100%': {
                transform: 'scale(1)',
                boxShadow: '0 0 0 0 rgba(33, 150, 243, 0)',
              },
            },
          }}
        >
          <Badge
            badgeContent={tradingHistory.length}
            color="error"
            max={99}
            invisible={tradingHistory.length === 0}
            sx={{
              '& .MuiBadge-badge': {
                top: -8,
                right: -8,
              },
            }}
          >
            {isAnalyzing ? <AssessmentIcon /> : <TradingIcon />}
          </Badge>
        </Fab>
      </Tooltip>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Trading Analysis
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {tradingHistory.length} analysis{tradingHistory.length !== 1 ? 'es' : ''} saved
          </Typography>
        </Box>

        <MenuItem onClick={handleOpenHistory} disabled={!hasHistory}>
          <ListItemIcon>
            <HistoryIcon />
          </ListItemIcon>
          <ListItemText>
            View History
          </ListItemText>
        </MenuItem>

        {activeAnalysis && (
          <MenuItem disabled>
            <ListItemIcon>
              <CodeIcon />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="body2">
                Current: {activeAnalysis.assetName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {activeAnalysis.status}
              </Typography>
            </ListItemText>
          </MenuItem>
        )}

        {hasHistory && (
          <>
            <Divider />
            <MenuItem onClick={handleClearHistory} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <ClearIcon />
              </ListItemIcon>
              <ListItemText>
                Clear History
              </ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* History Panel */}
      <TradingHistoryPanel
        open={historyPanelOpen}
        onClose={handleCloseHistory}
      />
    </>
  );
};

export default TradingFAB;